/**
 * @module BubbleTags
 */

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { Bubble, BubbleEvents, BubbleRB, BubbleTag } from 'rainbow-web-sdk/models/bubble.model';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { XmppService } from '../core';
import xmlParser from "rainbow-web-sdk/utils/xmlParser";
import { stringifyError } from '../../utils/stringifyError';
import { RBEvent } from '../../models';
import { Subject, Subscription, filter } from 'rxjs';
import { Service } from '../service';
import type { StropheHandler } from 'rainbow-web-sdk';

/** @internal */
export const BUBBLE_TAG_SVC = 'BubbleTagService';

/**
 * @eventProperty
 * This enum lists all the `RBEvent` events generated by the BubbleTagService.
 * They can be listened to using the `bubbleTagService.subscribe()` API.
 */
export enum BubbleTagServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is send when the bubbleTag service is started
     */
    ON_SERVICE_STARTED = 'ON_SERVICE_STARTED',

    /**
     * @eventProperty
     * This `RBEvent` is send when the tag list is modified
     */
     ON_TAGS_UPDATED = 'ON_TAGS_UPDATED'
}

export interface BubbleTagService {

    /**
     * Subscribe to the bubbleTag service events; All events send to the callback are of type `RBEvent`
     * @param callback - The callback function that will be called when events are received
     */
    subscribe(handler: (event: RBEvent<BubbleTagServiceEvents>) => any, eventNames?: BubbleTagServiceEvents | BubbleTagServiceEvents[]): Subscription;

    /**
     * Returns an array of all available bubble tags
     */
    getAllBubbleTags(): BubbleTag[];

    /**
     * Return the bubble tags hashmap (key = tagName)
     */
    getBubbleTagsMap(): Record<string, BubbleTag>;

    /**
     * Method to create a new bubble tag
     * @param bubble - The bubble to hang the tag on
     * @param tagName - Name of the tag to create
     * @param tagColor - Color of the tag to create in hexadecimal color code (default #ff9900)
     */
    createBubbleTag(bubble: Bubble, tagName: string, tagColor?: string): Promise<BubbleTag>;

    /**
     * Attach to the given bubble a list of existing tags
     * Please note that this new tag list will override current list
     * @param bubble - the bubble to tag
     * @param tags - array of tags
     */
    setBubbleTags(bubble: Bubble, tags: BubbleTag[]): Promise<void>;

    /**
     * Find local bubbles by tag
     * Takes into account tags separated by space
     * @param searchedTag - tag to find
     * @param exactMatch - whether we find exact matching tags
     */
    findBubblesByTag(searchedTag: string, exactMatch?: boolean): Promise<BubbleRB[]>;

    /**
     * This method deletes a tag from one or more bubbles.
     * @param tagName - Name of tag to remove
     * @param bubbles - List of bubbles from which to remove the tag
     */
    deleteBubbleTag(tagName: string, bubbles: Bubble[]): Promise<void>;

    /**
     * This method permits to update a bubble tag  (name and/or color).
     * @param tag - tag to rename.
     * @param newTagName - new tag name.
     * @param color - color value for the tag.
     */
    updateBubbleTag(tag: BubbleTag, newTagName: string, color?: string): Promise<void>

}

/** @internal */
interface RoomAllTags<T extends string = string> {
    tags: T[]
    tagsDetails?: {
        tag: T;
        color: `#${string}`
    }[]
}

/** @internal */
export class BubbleTagServiceRB extends Service implements BubbleTagService {

    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private portalURL: string = config.restServerUrl + '/api/rainbow/enduser/v1.0/';
    private bubbleService: BubbleServiceRB = BubbleServiceRB.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private xmppHandler?: StropheHandler;
    private findBubblesReady: boolean = false;
    private findBubblesPromise?: Promise<void>;
    private rxSubject: Subject<RBEvent<BubbleTagServiceEvents>> = new Subject<RBEvent<BubbleTagServiceEvents>>();
    public allTags: BubbleTag[] = [];
    public tagsMap: Record<string, BubbleTag> = {};


    // ************************************************************
    // ** LIFE CYCLE STUFF
    // ************************************************************
    public static getInstance() { return Service.create(BUBBLE_TAG_SVC, BubbleTagServiceRB); }
    public static build() { return new BubbleTagServiceRB(); }

    private constructor() { super(); }

    public async start(): Promise<void> {
        this.logger.info('');
        this.logger.info('[bubbleTagService] === STARTING ===');
        await this.init();
        this.sendEvent(BubbleTagServiceEvents.ON_SERVICE_STARTED);
        this.logger.info('[bubbleTagService] === STARTED ===');
    }

    public async reconnect(): Promise<void> {
        this.logger.info(`[BubbleTagService] reconnect`);
        await this.init();
        this.sendEvent(BubbleTagServiceEvents.ON_TAGS_UPDATED);
    }

    public async stop(): Promise<void> {
        this.logger.info('');
        this.logger.info('[bubbleTagService] === STOPPING ===');
        this.logger.info('[bubbleTagService] === STOPPED ===');
    }

    /**
     *  Subscribe to updates from the service (all events are of RBEvent type);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     */
    public subscribe(handler: (event: RBEvent<BubbleTagServiceEvents>) => any, eventNames?: BubbleTagServiceEvents | BubbleTagServiceEvents[]): Subscription {
        const observable = eventNames ? this.rxSubject.pipe(filter((event: RBEvent<BubbleTagServiceEvents>) => {
            if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
            return event.name === eventNames;
        })) : this.rxSubject;

        return observable.subscribe((event: RBEvent<BubbleTagServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[BubbleTagService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * BubbleTag service events send to other services via sendEvent()
     * Can listen to it via bubbleTagService.subscribe() API
     * @param name - the name of the event
     * @param data - the data to be send
     */
    public sendEvent(name: BubbleTagServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create<BubbleTagServiceEvents, any>(name, data));
    }

    /**
     * This init methods executes two subsequents requests to the server.
     * 1) First, it get the list of existing tags (name and color) and build a tag hashmap indexed by the tag name.
     * This hashmap contains the following structure (name, color, bubbleIds = [] ).
     * 2) Then a second request retreive for each tag name the list of bubbleIds with this tag
     * These info allow us to fill the bubbleIds entry of the hashmap.
     * 3) Finnaly the allTags array is filled by hashmap values.
     */
    private async init(): Promise<void> {
        try {
            this.logger.info(`[BubbleTagService] init`);

            // Get all tags
            const urlTags = `${this.portalURL}rooms/tags/all`;
            const headers = this.authService.getRequestHeader();
            const tagsResponse: Response = await fetch(urlTags, { method: 'GET', headers });
            if (!tagsResponse.ok) { throw (tagsResponse); }
            const tagsData: RoomAllTags = (await tagsResponse.json()).data;

            // Create the tagMap
            this.tagsMap = (tagsData.tagsDetails ?? []).reduce((tagsMap : Record<string, BubbleTag>, item: any) => {
                tagsMap[item.tag] = { name: item.tag, color: item.color };
                return tagsMap;
            }, {});

            // Get associated bubbles info
            if (tagsData.tags.length > 0) {
                const tagsRequest = tagsData.tags.map((tagName: string) => `tag=${tagName}`).join('&');
                const urlBubbles = `${this.portalURL}rooms/tags?${tagsRequest}&owntag=true`;
                const bubblesResponse = await fetch(urlBubbles, { method: 'GET', headers });
                if (!bubblesResponse.ok) { throw (bubblesResponse); }
                const bubblesData: { rooms: { roomId: string; tags: { tag: string; color }[] }[] } = (await bubblesResponse.json()).data;
                bubblesData.rooms.forEach((room) => {
                    room.tags.forEach((tag) => {
                        const name = tag.tag;
                        if (!this.tagsMap[name]) this.tagsMap[name] = { name: name, color: tag.color };
                        if (!Array.isArray(this.tagsMap[name].bubbleIds)) this.tagsMap[name].bubbleIds = [];
                        this.tagsMap[name].bubbleIds.push(room.roomId);
                    });
                });
            }
            // Store the tags array
            this.allTags = Object.values(this.tagsMap);

            // Attach event listener
            this.attachListeners();
        }
        catch (error) {
            this.logger.error(`[BubbleTagService] init - failure - ${stringifyError(error)}`);
        }
    }

    private attachListeners(): void {
        if (this.xmppHandler) { this.xmppService.deleteHandler(this.xmppHandler); this.xmppHandler = undefined; }
        // @ts-expect-error -- strophe isn't supposed to accept promise and doesn't support it, but be need it to works
        this.xmppHandler = this.xmppService.addHandler(async (stanza) => {
            const stanzaElem = xmlParser(stanza);
            const conversationElems = stanzaElem.find('conversation');
            const roomElems = stanzaElem.find('room');

            // Handle bubble deletion, should remove bubbleId for tags cache
            if (conversationElems.length > 0) {
                const action = conversationElems.attr('action');
                if (action === 'delete') {
                    const type = conversationElems.find('type').text();
                    const bubbleId = conversationElems.find('peerId').text();
                    const bubble = await this.bubbleService.getBubble(bubbleId);

                    if (type === 'room' && bubbleId) {
                        // Clean bug tags
                        if (bubble) bubble.tags = [];

                        // Update allTags array
                        this.removeBubbleFromCache(bubbleId);
                        this.allTags.length = 0;
                        Object.values(this.tagsMap).forEach(tag => this.allTags.push(tag));
                    }
                }
            }

            if (roomElems.length > 0) {
                const bubbleId = roomElems.attr('roomid');
                const bubble = await this.bubbleService.getBubble(bubbleId);
                const tagsElems = stanzaElem.find('tags');
                const tagsAction = tagsElems?.attr('action');
                if (tagsElems.length) {
                    this.logger.infoEvent(`[BubbleTagService] - onTagsEvent ${tagsAction}`);
                    const tagElems = stanzaElem.find('tag');

                    // No tag elements = remove all tags from this bubble
                    if (tagElems.length === 0) {
                        bubble.tags.forEach(tag => this.removeBubbleFromTagInCache(tag, bubbleId));
                        bubble.tags = [];
                    }

                    // Add/Remove tag elements
                    else {
                    // Create the new bubble tags array
                        const newBubbleTags = tagElems.map<BubbleTag>((tagElem: any) => {
                            return { name:tagElem.attr('value'), color: tagElem.attr('color') };
                        });

                        // New tags
                        const newTags = newBubbleTags.filter(item => !bubble.tags.find(tag => item.name === tag.name));
                        newTags.forEach(tag => this.addBubbleToTagInCache(tag, bubbleId));

                        // Removed tags
                        const removedTags = bubble.tags.filter(item => !newBubbleTags.find(tag => item.name === tag.name));
                        removedTags.forEach(tag => this.removeBubbleFromTagInCache(tag, bubbleId));

                        // Update bubble tags array
                        bubble.tags = newBubbleTags;
                    }

                    // Update allTags array
                    this.allTags.length = 0;
                    Object.values(this.tagsMap).forEach(tag => this.allTags.push(tag));
                    bubble.sendEvent(BubbleEvents.ON_TAGS_UPDATED);
                }
            }
            this.sendEvent(BubbleTagServiceEvents.ON_TAGS_UPDATED);
        }, null, 'message', 'management');
    }

    private removeBubbleFromCache(bubbleId: string): void {
        Object.values(this.tagsMap).forEach(tag => {
            tag.bubbleIds = tag.bubbleIds.filter(tagBubbleId => tagBubbleId !== bubbleId);
            if (tag.bubbleIds.length === 0)
                delete this.tagsMap[tag.name];
        });
    }

    private removeBubbleFromTagInCache(tag: BubbleTag, bubbleId: string) {
        const bubbleIds = this.tagsMap[tag.name].bubbleIds;
        const bulleIdIndex = bubbleIds.indexOf(bubbleId);
        if (bulleIdIndex !== -1) bubbleIds.splice(bulleIdIndex, 1);
        if (bubbleIds.length === 0) delete this.tagsMap[tag.name];
    }

    private addBubbleToTagInCache(tag: BubbleTag, bubbleId: string) {
        // If tag entry doesn't existe, create it
        if (!this.tagsMap[tag.name]) {
            this.tagsMap[tag.name] = { name: tag.name, color: tag.color, bubbleIds : [bubbleId] };
        }
        // Else update it
        else {
            const bubbleIds = this.tagsMap[tag.name].bubbleIds;
            const bubbleIdIndex = bubbleIds.indexOf(bubbleId);
            if (bubbleIdIndex === -1) bubbleIds.push(bubbleId);
        }
    }

    public getAllBubbleTags(): BubbleTag[] {
        return this.allTags;
    }

    public getBubbleTagsMap(): Record<string, BubbleTag> {
        return this.tagsMap;
    }

    public async createBubbleTag(bubble: Bubble, tagName: string, tagColor: string = "#ff9900"): Promise<BubbleTag> {
        // Check if this tag already exists
        const tag = this.allTags.find(tag => tag.name === tagName);
        if (tag) throw new Error(`The tag ${tagName} already exists`);

        // It's a new tag create it
        const newTag = { name: tagName, color: tagColor};
        await this.setBubbleTags(bubble, bubble.tags.concat([newTag]));
        return newTag as BubbleTag;
    }

    public async setBubbleTags(bubble: Bubble, tags: BubbleTag[]): Promise<void> {
        try {
            if (!bubble || !tags || tags.length < 1) throw new Error(`[BubbleTagService] setBubbleTags -- no bubble or tags empty/undefined`);
            const url = `${this.portalURL}rooms/${bubble.dbId}/tags`;
            const headers = this.authService.getPostHeader();
            const serverTags = tags.map(tag => { return { tag: tag.name, color: tag.color }; });
            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({ "tags": serverTags }) });
            if (!response.ok) throw response;
            this.logger.debug(`[BubbleTagService] setBubbleTags -- SUCCESS -- `);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setBubbleTags');
            this.logger.error(`[BubbleTagService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateBubbleTag(tag: BubbleTag, newTagName: string, color?: string): Promise<void> {

        const newtag = this.tagsMap[newTagName];
        if (newtag && tag.color === color) throw new Error(`The tag ${newTagName} already exists`);

        // Update server side
        const newColor = color ? color : tag.color;
        const url = `${this.portalURL}rooms/tags`;
        const headers = this.authService.getPostHeader();
        const body = { tag: tag.name, newTagName: newTagName, color: newColor, rooms: tag.bubbleIds };
        const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) });
        if (!response.ok) { throw (response); }
        this.logger.debug(`[BubbleTagService] renameTag -- SUCCESS -- `);

        // Update tag
        tag.color = color;
        tag.name = newTagName;
    }

    public async deleteBubbleTag(tagName: string, bubbles: Bubble[]): Promise<void> {
        try {
            if (!tagName || !bubbles || bubbles.length < 1) throw new Error(`[BubbleTagService] deleteBubbleTag -- no tag or no bubbles`);

            const tag = this.allTags.find(tag => tag.name === tagName);
            if (!tag) throw new Error(`The tag ${tagName} does not exist`);

            const url = `${this.portalURL}rooms/tags`;
            const headers = this.authService.getPostHeader();
            const rooms = bubbles.map(bubble => bubble.dbId);
            const response: Response = await fetch(url, { method: 'DELETE', headers, body: JSON.stringify({ tag: tagName, rooms: rooms }) });
            if (!response.ok) { throw (response); }
            this.logger.debug(`[BubbleTagService] deleteBubbleTag -- SUCCESS -- `);
            this.sendEvent(BubbleTagServiceEvents.ON_TAGS_UPDATED);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteBubbleTag');
            this.logger.error(`[BubbleTagService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async findBubblesByTag(searchedTag: string, exactMatch: boolean = false): Promise<BubbleRB[]> {
        try {
            // Lazy load missing tagged bubbles
            if (!this.findBubblesReady) await this.lazyLoadMissingTaggedBubbles();

            if (!searchedTag) throw new Error(`[BubbleTagService] findBubblesByTag -- no tag to search`);
            const searchedTagNames = exactMatch ? [searchedTag] : searchedTag.split(/[" *"]/).filter(Boolean);

            // Identify searched bubble ids
            //reduce does not keep reference to THIS
            const self = this;
            const bubbleIdsSet = searchedTagNames.reduce((bubbleIds: Set<string>, tagName: any) => {
                self.tagsMap[tagName]?.bubbleIds?.forEach(bubbleId => bubbleIds.add(bubbleId));
                return bubbleIds;
            }, new Set<string>());

            // Get bubbles
            return Array.from(bubbleIdsSet).map(bubbleId => this.bubbleService.getBubbleFromCache(bubbleId) as BubbleRB);
        }
        catch {
            const err = new Error(`[BubbleTagService] findBubblesByTag -- Issue with search`);
            this.logger.error(err.message);
            throw err;
        }
    }

    private lazyLoadMissingTaggedBubbles(): Promise<void> {
        if (!this.findBubblesPromise) this.findBubblesPromise = new Promise((resolve: any) => {
            this.logger.info(`[BubbleTagService] lazyLoadMissingTaggedBubbles()`);
            const missingBubbleIdsSet = this.allTags.reduce((bubbleIds: Set<string>, tag: BubbleTag) => {
                tag.bubbleIds?.forEach(bubbleId => { if (!this.bubbleService.getBubbleFromCache(bubbleId)) bubbleIds.add(bubbleId); });
                return bubbleIds;
            }, new Set<string>());
            const missingBubbleIds = Array.from(missingBubbleIdsSet);
            this.bubbleService.getServerBubblesByIds(missingBubbleIds)
                .then(() => {
                    resolve();
                    this.findBubblesReady = true;
                    this.findBubblesPromise = undefined;
                });
        });
        return this.findBubblesPromise;
    }

    public findBubblesByTag2(searchedTag: string, exactMatch: boolean = false): BubbleRB[] {
        /*try {
            if (!searchedTag) throw new Error(`[BubbleTagService] findBubblesByTag -- no tag to search`);
            // Multiple tag search
            const searchedTagArray = exactMatch ? [searchedTag] : searchedTag.split(/[" *"]/).filter((elem) => { return elem !== ""; });
            const bubbleIds = searchedTagArray.reduce((bubbleIds: string[], tagNameSearched:string) => {
                bubbleIds.concat(this.tagsMap[tagNameSearched].bubbleIds);
                return bubbleIds;
            }, []);

        }
        catch {
            const err = new Error(`[BubbleTagService] findBubblesByTag -- Issue with search`);
            this.logger.error(err.message);
            throw err;
        }*/
        try {
            if (!searchedTag) throw new Error(`[BubbleTagService] findBubblesByTag -- no tag to search`);

            // multiple tag search
            const searchedTagArray = exactMatch ? [searchedTag] : searchedTag.split(/[" *"]/).filter((elem) => { return elem !== ""; });

            const foundBubbles: BubbleRB[] = [];
            searchedTagArray.forEach((tagSearched: string) => {
                this.bubbleService.getBubblesFromCache().forEach((bubble: Bubble) => {
                    bubble.tags.forEach(bubbleTag => {
                        if (!exactMatch && bubbleTag.name.toLowerCase().includes(tagSearched.toLowerCase()) && !foundBubbles.some((addedBubble) => { return addedBubble.dbId === bubble.dbId; })) {
                            foundBubbles.push(bubble as BubbleRB);
                        }
                        else if (bubbleTag.name === tagSearched && !foundBubbles.some((addedBubble) => { return addedBubble.dbId === bubble.dbId; })) {
                            foundBubbles.push(bubble as BubbleRB);
                        }
                    });
                });
            });
            return foundBubbles;
        }
        catch {
            const err = new Error(`[BubbleTagService] findBubblesByTag -- Issue with search`);
            this.logger.error(err.message);
            throw err;
        }
    }

}
